import { ChangeEvent, useMemo, useState } from 'react';
import {
  Autocomplete,
  Card,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  useTheme,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DataGrid, gridStringOrNumberComparator } from '@mui/x-data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CompliancePlip from '../../../../components/CompliancePlip';
import { ChildPracticeMetadata, Groups } from '../../../../types';
import { standardDateFormat } from '../../../../helpers/date';
import { getComplianceColor } from '../../../../helpers/compliance';
import { useNavigate } from 'react-router-dom';

const Unchecked = <CheckBoxOutlineBlankIcon fontSize="small" />;
const Checked = <CheckBoxIcon fontSize="small" />;

type AllowedCategories = 'amalgam' | 'mwaste' | 'pharma';

interface WasteMangementTableProps {
  category: AllowedCategories;
  childPractices: ChildPracticeMetadata[];
  complianceFilter: number | undefined;
  groups: Groups[];
  loading: boolean;
  handleComplianceFilter: (event: SelectChangeEvent<number | undefined>) => void;
}

const WasteMangementTable: React.FC<WasteMangementTableProps> = (props) => {
  const { category, childPractices, complianceFilter, groups, loading, handleComplianceFilter } = props
  const link = category === 'mwaste' ? 'medical-waste' : category === 'pharma' ? 'pharmaceutical-waste' : 'amalgam-waste';

  const theme = useTheme();
  const navigate = useNavigate();

  const [groupFilter, setGroupFilter] = useState<Groups[]>([]);
  const [search, setSearch] = useState<string>('');

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearch(value);
  };

  const filteredPractices = useMemo(() => {
    let practices = structuredClone(childPractices) ?? [];

    if (search.length > 0) {
      const cleanSearch = search.toLowerCase();
      practices = practices.filter(({ customer_number, name, street, city, state }) => {
        return (
          customer_number.includes(cleanSearch) ||
          name.toLowerCase().includes(cleanSearch) ||
          street.toLowerCase().includes(cleanSearch) ||
          city.toLowerCase().includes(cleanSearch) ||
          state.toLowerCase().includes(cleanSearch)
        );
      });
    }

    if (complianceFilter) {
      practices = practices.filter((practice) => practice[`${category}_status`] === complianceFilter);
    }

    if (groupFilter.length) {
      const members = groupFilter.flatMap((group) => group?.members.map((member) => member.customer_number));
      if (members) {
        practices = practices.filter((customer) => members.includes(customer.customer_number));
      }
    }

    return practices;
  }, [childPractices, complianceFilter, groupFilter, search]);

  return (
    <Card variant="outlined">
      <div className="box-header b-b">{filteredPractices.length} Practices</div>
      <div className="flex flex-space-between m-16">
        <div className="flex">
          <FormControl sx={{ mr: 2, minWidth: 200 }} size="small">
            <InputLabel>Filter by Compliance</InputLabel>
            <Select value={complianceFilter ?? ''} label="Filter by Compliance" onChange={handleComplianceFilter}>
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value={3}>Action Required</MenuItem>
              <MenuItem value={2}>Attention Needed</MenuItem>
              <MenuItem value={1}>Up to Date</MenuItem>
              <MenuItem value={4}>Service Inactive</MenuItem>
            </Select>
          </FormControl>
          {groups.length > 0 && (
            <Autocomplete
              multiple
              onChange={(event, newValue) => {
                setGroupFilter(newValue);
              }}
              options={groups}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={Unchecked} checkedIcon={Checked} style={{ marginRight: 8 }} checked={selected} />
                  {option.name}
                </li>
              )}
              size="small"
              style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
              renderInput={(params) => <TextField {...params} label="Filter by Group" />}
            />
          )}
        </div>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search"
          onChange={handleSearch}
          InputProps={{
            endAdornment: (
              <IconButton>
                <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
              </IconButton>
            )
          }}
        />
      </div>
      <DataGrid
        sx={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', borderRadius: '0' }}
        rows={filteredPractices.map((practice) => ({ ...practice, id: practice.customer_number }))}
        columns={[
          {
            field: `${category}_status`,
            headerName: 'Status',
            sortable: true,
            width: 100,
            renderCell: (params) => <CompliancePlip message={params.row[`${category}_message_short`]} status={params.row[`${category}_status`]} />,
            align: 'center',
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1;
              return (a, b, cellParams1, cellParams2) => {
                if (a === b) {
                  return 0;
                }

                if (a === 4 || a === null) {
                  return 1;
                }

                if (b === 4 || b === null) {
                  return -1;
                }

                return modifier * gridStringOrNumberComparator(a, b, cellParams1, cellParams2);
              };
            }
          },
          {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 500,
            renderCell: (params) => (
              <Link sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }} component="button" onClick={() => navigate(`/corp/practice/${params.row.customer_number}/${link}`)}>
                {params.row.name.trim()} ({params.row.customer_number})
              </Link>
            )
          },
          {
            field: 'city',
            headerName: 'Location',
            flex: 1,
            minWidth: 250,
            renderCell: (params) => `${params.row.city}, ${params.row.state}`
          },
          {
            field: `${category}_available_containers`,
            headerName: 'In Use',
            flex: 1,
            minWidth: 125,
            renderCell: (params) => (
              <span className={params.row[`${category}_available_containers`] === 0 ? 'color-danger' : ''}>
                {params.row[`${category}_available_containers`]}
              </span>
            )
          },
          {
            field: category === 'pharma' ? `${category}_processed_containers` : `${category}_destroyed_containers`,
            headerName: 'Processed',
            flex: 1,
            minWidth: 125
          },
          {
            field: category === 'pharma' ? `${category}_last_processed_date` : `${category}_last_destroyed_date`,
            headerName: 'Last Processed Date',
            flex: 1,
            minWidth: 125,
            renderCell: (params) => {
              const dateValue = category === 'pharma' ? params.row[`${category}_last_processed_date`] : params.row[`${category}_last_destroyed_date`];

              return standardDateFormat(dateValue);
            },
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1;
              return (a, b, cellParams1, cellParams2) => {
                if (a === b) {
                  return 0;
                }

                if (a === null) {
                  return 1;
                }

                if (b === null) {
                  return -1;
                }

                return (
                  modifier *
                  gridStringOrNumberComparator(a, b, cellParams1, cellParams2)
                );
              };
            }
          },
          {
            field: `${category}_message_short`,
            headerName: 'Message',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
              <span className={getComplianceColor(params.row[`${category}_status`])}>{params.row[`${category}_message_short`]}</span>
            )
          }
        ]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25
            }
          },
          sorting: {
            sortModel: [
              {
                field: `${category}_status`,
                sort: 'desc'
              }
            ]
          }
        }}
        pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        loading={loading}
        autoHeight
        density="compact"
      />
    </Card>
  );
};

export default WasteMangementTable;
